import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { MobileFrame } from "@/components/MobileFrame";
import { Hairline, Icon, RubberStamp, Swatch, type SwatchKind } from "@/components/primitives";
import { PostCarousel } from "@/components/PostCarousel";
import { CommentsInline } from "@/components/CommentsInline";
import { ReportDialog } from "@/components/ReportDialog";
import { ReportFastFashionDialog } from "@/components/ReportFastFashionDialog";
import { ModeratorVoteBar } from "@/components/ModeratorVoteBar";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useBlockUser, useBlockedUsers } from "@/hooks/useBlockedUsers";
import { useModeratorStatus } from "@/hooks/useModeratorStatus";
import { useLoves } from "@/hooks/useLoves";
import { useCommentCounts } from "@/hooks/useComments";
import { toast } from "@/lib/toast";
import { ensureUrlProtocol, parseMarketplaceLinks } from "@/lib/marketplaces";
import { logListingClick } from "@/lib/listingClicks";

const menuItemStyle: React.CSSProperties = {
  width: "100%",
  padding: "12px 14px",
  textAlign: "left",
  background: "transparent",
  border: "none",
  borderBottom: "1px solid rgba(20,14,7,0.08)",
  color: "hsl(var(--ink))",
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: 13,
  letterSpacing: "0.04em",
  cursor: "pointer",
};

type MarketplaceLink = { name: string; url: string };

type RawResult = {
  assessment?: {
    verdict_reasoning?: string;
    era_indicators?: string[];
    overall_confidence?: string;
  };
  pricing?: {
    guidance?: {
      recommended_range?: string;
    };
  };
  [k: string]: unknown;
};

type Post = {
  id: string;
  title: string;
  brand: string | null;
  era: string | null;
  category: string | null;
  material: string | null;
  color: string | null;
  size: string | null;
  condition: string | null;
  caption: string | null;
  verdict: string | null;
  confidence: number | null;
  img: string;
  photos: string[];
  user_id: string;
  handle: string;
  display_name: string | null;
  avatar: string;
  created_at: string;
  status: string | null;
  marketplace_links: MarketplaceLink[] | null;
  /** Seller's saved shop URLs from their profile (profiles.marketplace_profiles).
   *  Used as a fallback target for "Shop in person" CTAs when the listing
   *  itself doesn't carry a URL — the most useful action for a face-to-face
   *  sale is "contact this seller on whatever platform they already use." */
  seller_shops: MarketplaceLink[];
  result: RawResult | null;
  suspended_at: string | null;
};

function getVerificationSummary(result: RawResult | null): string | null {
  if (!result) return null;
  // Prefer an explicit verdict_reasoning field if the AI starts returning it.
  const reasoning = result.assessment?.verdict_reasoning;
  if (reasoning && reasoning.length > 10) {
    const sentences = reasoning.match(/[^.!?]+[.!?]+/g) ?? [reasoning];
    return sentences.slice(0, 2).join(" ").trim();
  }
  // Fall back to era_indicators — these are the key dating signals.
  const indicators = result.assessment?.era_indicators;
  if (Array.isArray(indicators) && indicators.length > 0) {
    return indicators.slice(0, 2).join(". ").trim() + ".";
  }
  return null;
}

export default function Post() {
  const nav = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, session } = useAuth();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showComments, setShowComments] = useState(false);
  // Ref on the comments wrapper so tapping the Notes pill scrolls the
  // section into view. CommentsInline already auto-focuses its textarea
  // (with preventScroll), this adds the page-scroll piece.
  const commentsWrapperRef = useRef<HTMLDivElement | null>(null);
  const handleNotesClick = () => {
    const willOpen = !showComments;
    setShowComments(willOpen);
    if (willOpen) {
      // Wait one tick for the comments body to render before scrolling so
      // the target element has its expanded height.
      window.setTimeout(() => {
        commentsWrapperRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 60);
    }
  };
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [ffReportOpen, setFfReportOpen] = useState(false);
  const block = useBlockUser();
  const { data: blockedSet } = useBlockedUsers();
  const { data: modState } = useModeratorStatus();

  const ids = useMemo(() => (post ? [post.id] : []), [post]);
  const loves = useLoves(ids);
  const commentCounts = useCommentCounts(ids);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: scan, error: e1 } = await supabase
        .from("scans")
        .select("id,title,brand,era,category,material,color,size,condition,caption,verdict,confidence,img,photos,user_id,created_at,status,marketplace_links,result,posted,suspended_at")
        .eq("id", id)
        .maybeSingle();
      if (cancelled) return;
      if (e1 || !scan || !scan.posted) {
        setError("This post isn't available.");
        setLoading(false);
        return;
      }
      const { data: prof } = await (supabase as any)
        .from("profiles_public")
        .select("handle, display_name, avatar, marketplace_profiles")
        .eq("id", scan.user_id)
        .maybeSingle();
      if (cancelled) return;
      const sellerShops = parseMarketplaceLinks(
        (prof as { marketplace_profiles?: unknown } | null)?.marketplace_profiles,
      ).filter((s) => s.url.trim().length > 0);

      const rawResult = scan.result as RawResult | null;
      const rawLinks = Array.isArray(scan.marketplace_links)
        ? (scan.marketplace_links as MarketplaceLink[])
        : null;

      setPost({
        id: scan.id,
        title: scan.title,
        brand: scan.brand,
        era: scan.era,
        category: scan.category,
        material: scan.material,
        color: scan.color,
        size: scan.size,
        condition: scan.condition,
        caption: scan.caption,
        verdict: scan.verdict,
        confidence: scan.confidence,
        img: scan.img,
        photos: Array.isArray(scan.photos) ? scan.photos : [],
        user_id: scan.user_id,
        created_at: scan.created_at,
        status: (scan as Record<string, unknown>).status as string | null ?? null,
        marketplace_links: rawLinks?.length ? rawLinks : null,
        seller_shops: sellerShops,
        result: rawResult,
        suspended_at: ((scan as Record<string, unknown>).suspended_at as string | null) ?? null,
        handle: prof?.handle ?? "collector",
        display_name: prof?.display_name ?? null,
        avatar: prof?.avatar ?? "olive",
      });
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id]);

  const sharePost = async () => {
    if (!post) return;
    const url = `${window.location.origin}/post/${post.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `${post.title} — via Fig`, url });
        return;
      }
    } catch (e) {
      if ((e as DOMException)?.name === "AbortError") return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  const isOwn = !!user && !!post && post.user_id === user.id;
  const viewerId = session?.user?.id;
  const ownerId = post?.user_id ?? null;
  const isSignedIn = !!viewerId;
  const isVerifiedMod = modState?.status === "approved";
  const isSuspended = post?.suspended_at != null;
  const heroImg = post?.photos[0] || post?.img;
  const heroIsUrl = typeof heroImg === "string" && /^https?:\/\//.test(heroImg);

  // Hide suspended posts from non-owner viewers entirely.
  if (post && isSuspended && !isOwn) {
    return (
      <MobileFrame>
        <div style={{ padding: 32, color: "hsl(var(--ink))", textAlign: "center" }}>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 24 }}>
            Post unavailable
          </h2>
          <p style={{ marginTop: 8, opacity: 0.7 }}>This post is no longer publicly visible.</p>
        </div>
      </MobileFrame>
    );
  }

  // Verification state
  const isVerifiedPass = post?.verdict === "PASS";
  const hasVerification = post?.verdict === "PASS" || post?.verdict === "FAIL";
  const isListed = post?.status === "listed";
  const isKept = post?.status === "kept";
  const summary = post ? getVerificationSummary(post.result) : null;
  const recommendedRange = post?.result?.pricing?.guidance?.recommended_range ?? null;

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", paddingBottom: 100 }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: "1px solid hsl(var(--sand-deep))" }}>
          <button onClick={() => nav(-1)} style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}>
            {Icon.chev(18, "l")}
          </button>
          <span className="caps" style={{ color: "hsl(var(--ink-2))" }}>Post</span>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <button onClick={sharePost} aria-label="Share" style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}>
              {Icon.share(16)}
            </button>
            {post && (
              <button
                type="button"
                aria-label="More actions"
                onClick={() => setMenuOpen(true)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "hsl(var(--ink))",
                  fontSize: 22,
                  padding: "2px 8px",
                  cursor: "pointer",
                  lineHeight: 1,
                }}
              >
                ⋯
              </button>
            )}
          </div>
        </div>

        {loading && (
          <div style={{ padding: 40, textAlign: "center", fontFamily: "'Fraunces', 'Playfair Display', serif", color: "hsl(var(--ink-2))" }}>
            loading…
          </div>
        )}
        {!loading && error && (
          <div style={{ padding: 40, textAlign: "center", fontFamily: "'Fraunces', 'Playfair Display', serif", color: "hsl(var(--ink-2))" }}>
            {error}
          </div>
        )}

        {!loading && post && (
          <>
            {/* Suspended banner — owner only */}
            {isSuspended && isOwn && (
              <div
                style={{
                  padding: "12px 16px",
                  background: "hsl(var(--cream))",
                  borderTop: "1px solid hsl(var(--ink))",
                  borderBottom: "1px solid hsl(var(--ink))",
                  color: "hsl(var(--ink))",
                  fontSize: 13,
                  lineHeight: 1.4,
                }}
              >
                <strong style={{ fontFamily: "'Fraunces', serif" }}>Pending fig admin review</strong>
                <div style={{ opacity: 0.8, marginTop: 2 }}>
                  Hidden from public feeds while we look at it.
                </div>
              </div>
            )}

            {/* Hero photo. Three render paths:
                  1. Multiple URL photos → swipeable carousel (PostCarousel).
                     User can scroll horizontally through each photo with
                     dots + counter. Verified pieces show the VINTAGE stamp
                     on the first slide; unverified quick-posts show no
                     stamp (verdict=FAIL is just a NOT-NULL placeholder).
                  2. Single URL photo → natural-height image (no forced
                     aspect ratio, no carousel chrome).
                  3. No real photo → swatch fallback at 1:1. */}
            {(() => {
              const urlPhotos = (post.photos ?? []).filter(
                (s): s is string => typeof s === "string" && /^https?:\/\//.test(s),
              );
              const unverified = !post.result;
              if (urlPhotos.length > 1) {
                return (
                  <PostCarousel
                    photos={urlPhotos}
                    alt={post.title}
                    verdict={post.verdict ?? "FAIL"}
                    conf={post.confidence ?? 0}
                    unverified={unverified}
                  />
                );
              }
              return (
                <div style={{ position: "relative", borderBottom: "1px solid hsl(var(--ink))" }}>
                  {heroIsUrl ? (
                    <img
                      src={heroImg as string}
                      alt={post.title}
                      style={{ width: "100%", height: "auto", display: "block", background: "hsl(var(--paper-3))" }}
                    />
                  ) : (
                    <Swatch kind={post.img as SwatchKind} style={{ width: "100%", aspectRatio: "1/1" }} />
                  )}
                  {isVerifiedPass && (
                    <div style={{ position: "absolute", top: 14, right: 14 }}>
                      <RubberStamp label="VINTAGE" sub="" color="hsl(var(--rust))" size={84} />
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Author row — sits between the photo and the title so the
                hero imagery leads and the seller identity is the bridge
                into the metadata block. */}
            <button
              onClick={() => nav(isOwn ? "/you" : `/profile/${encodeURIComponent("@" + post.handle)}`)}
              style={{
                display: "flex", alignItems: "center", gap: 10, width: "100%",
                padding: "12px 16px", background: "transparent", border: "none",
                borderBottom: "1px solid hsl(var(--sand-deep))",
                cursor: "pointer", textAlign: "left",
              }}
            >
              <Swatch kind={(post.avatar as SwatchKind) || "olive"} style={{ width: 36, height: 36, borderRadius: "50%", border: "1px solid hsl(var(--ink))" }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 15, color: "hsl(var(--ink))" }}>
                  {post.display_name || `@${post.handle}`}
                </div>
                <div className="caps" style={{ color: "hsl(var(--ink-2))", marginTop: 2, fontSize: 9 }}>
                  @{post.handle}
                </div>
              </div>
              {!isOwn && Icon.chev(14)}
            </button>

            {/* Title + verification info */}
            <div style={{ padding: "18px 18px 0" }}>
              <div className="caps" style={{ color: "hsl(var(--rust))" }}>
                {(() => {
                  // Filter out "indeterminate" — the model emits it as the
                  // era_confidence sentinel when it can't date a piece, and
                  // it sometimes leaks into the era column. On the kicker
                  // line it reads as a weird non-era value. For unverified
                  // posts where everything is user-supplied, hide it
                  // entirely; for verified items the same filter keeps
                  // visitors from seeing a useless "INDETERMINATE" tag.
                  const era = post.era && !/^indeterminate$/i.test(post.era.trim())
                    ? post.era
                    : null;
                  const parts = [era, post.brand].filter(Boolean);
                  if (parts.length > 0) return parts.join(" · ");
                  // No era / brand and no verification → "Not verified"
                  // is more informative than the bare em-dash.
                  if (!post.result) return "Not verified";
                  return "—";
                })()}
              </div>
              <h1 style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800,
                fontSize: 26, lineHeight: 1.1, color: "hsl(var(--ink))",
                margin: "6px 0 0", letterSpacing: "-0.01em",
              }}>{post.title}</h1>

              {/* FIG Verified badge */}
              {isVerifiedPass && (
                <div style={{
                  marginTop: 10, display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "4px 8px", background: "hsl(var(--rust))",
                }}>
                  <div style={{
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
                    letterSpacing: "0.14em", textTransform: "uppercase",
                    color: "hsl(var(--rust-fg))", fontWeight: 700,
                  }}>
                    FIG Verified
                  </div>
                </div>
              )}

              {/* Verdict statement. Three states:
                    1. Item actually ran through fig-verify AND passed
                         → "Authenticated as vintage"
                    2. Item ran through fig-verify AND failed
                         → "Reviewed — did not pass vintage criteria"
                    3. Item is a quick-post (no `result`) — verdict on the
                         row is a NOT-NULL placeholder, NOT a real fig-verify
                         outcome → "This item has not been verified by fig"
                  v2 dropped the numeric confidence percentage; PASS/FAIL
                  is the only signal shown to viewers, with optional
                  category_advisory below. */}
              {!post.result ? (
                <div style={{
                  marginTop: 8, fontFamily: "'Fraunces', 'Playfair Display', serif",
                  fontSize: 14, color: "hsl(var(--ink-2))",
                }}>
                  This item has not been verified by fig
                </div>
              ) : hasVerification && (
                <div style={{
                  marginTop: 8, fontFamily: "'Fraunces', 'Playfair Display', serif",
                  fontSize: 14, color: "hsl(var(--ink-2))",
                }}>
                  {isVerifiedPass
                    ? "Authenticated as vintage"
                    : "Reviewed — did not pass vintage criteria"}
                </div>
              )}

              {/* One-line summary from assessment */}
              {isVerifiedPass && summary && (
                <div style={{
                  marginTop: 6, fontFamily: "'Fraunces', 'Playfair Display', serif",
                  fontSize: 13, color: "hsl(var(--ink))", lineHeight: 1.45,
                }}>
                  {summary}
                </div>
              )}

              {/* Info grid — Color removed to match the Result page */}
              {[post.category, post.material, post.size, post.condition].some(Boolean) && (
                <div style={{
                  marginTop: 14, display: "grid",
                  gridTemplateColumns: "1fr 1fr", gap: "8px 16px",
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                  color: "hsl(var(--ink-2))",
                }}>
                  {([
                    ["Category", post.category],
                    ["Material", post.material],
                    ["Size", post.size],
                    ["Condition", post.condition],
                  ] as const).filter(([, v]) => !!v).map(([k, v]) => (
                    <div key={k}>
                      <div className="caps" style={{ color: "hsl(var(--ink-2))", fontSize: 9 }}>{k}</div>
                      <div style={{ color: "hsl(var(--ink))", marginTop: 2 }}>{v}</div>
                    </div>
                  ))}
                </div>
              )}

              {post.caption && (
                <p style={{
                  marginTop: 16, fontFamily: "'Fraunces', 'Playfair Display', serif",
                  fontSize: 15, lineHeight: 1.5, color: "hsl(var(--ink))",
                }}>"{post.caption}"</p>
              )}
            </div>

            {/* Price + marketplace section */}
            <div style={{ padding: "14px 18px 0" }}>
              {isKept && (
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: "50%",
                    background: "hsl(var(--olive))", flexShrink: 0,
                  }} />
                  <div className="caps" style={{ color: "hsl(var(--olive))", fontSize: 9 }}>
                    In their closet
                  </div>
                </div>
              )}

              {isListed && (recommendedRange || post.marketplace_links) && (
                <>
                  {/* FIG's recommended-price band is owner-only data — buyers
                      browsing someone else's posted scan see only the
                      marketplace links (where the seller's actual asking
                      price lives), not FIG's algorithmic suggestion. Showing
                      the recommendation to non-owners would (1) leak the
                      seller's pricing intel and (2) anchor buyers to the
                      lower end of the band, undermining the seller. */}
                  {recommendedRange && isOwn && (
                    <div style={{ marginBottom: 12 }}>
                      <div className="caps" style={{ color: "hsl(var(--ink-2))", fontSize: 9, marginBottom: 2 }}>
                        Recommended price
                      </div>
                      <div style={{
                        fontFamily: "'Fraunces', 'Playfair Display', serif",
                        fontWeight: 800, fontSize: 22, color: "hsl(var(--ink))",
                      }}>
                        {recommendedRange}
                      </div>
                    </div>
                  )}
                  {post.marketplace_links && post.marketplace_links.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {post.marketplace_links.map((link) => {
                        const lowerName = link.name.toLowerCase();
                        const isInPerson = lowerName.startsWith("in person");
                        const isPersonalSite = lowerName === "personal site";
                        const trimmedUrl = (link.url ?? "").trim();
                        if (isInPerson) {
                          // In Person now stores either:
                          //   (a) a typed address ("123 Main St, Brooklyn"),
                          //   (b) a Google/Apple Maps share URL, or
                          //   (c) empty → fall back to the seller's saved
                          //       In Person field from their profile, then
                          //       their other shops as a last resort.
                          const looksLikeUrl =
                            /^https?:\/\//i.test(trimmedUrl) ||
                            /^(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/|$)/i.test(trimmedUrl);

                          // Listing-level URL takes priority.
                          if (trimmedUrl && looksLikeUrl) {
                            return (
                              <a
                                key={link.name}
                                href={ensureUrlProtocol(trimmedUrl)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-rust"
                                style={{ padding: "13px 16px", textDecoration: "none", textAlign: "center" }}
                                onClick={() => { if (!isOwn) void logListingClick(post.id, link.name); }}
                              >
                                Shop in person · open in maps →
                              </a>
                            );
                          }
                          // Listing-level plain-text address → non-clickable
                          // label with the address rendered as-is.
                          if (trimmedUrl) {
                            return (
                              <div
                                key={link.name}
                                style={{
                                  padding: "13px 16px",
                                  border: "1px dashed hsl(var(--ink))",
                                  background: "hsl(var(--paper-2, var(--cream)))",
                                  textAlign: "center",
                                  fontFamily: "'IBM Plex Mono', monospace",
                                  fontSize: 11,
                                  letterSpacing: "0.08em",
                                  textTransform: "uppercase",
                                  color: "hsl(var(--ink-2))",
                                }}
                              >
                                Shop in person · {trimmedUrl}
                              </div>
                            );
                          }
                          // No listing-level value → look at the seller's
                          // saved In Person on their profile.
                          const profileInPerson = post.seller_shops.find(
                            (s) => s.name.toLowerCase() === "in person",
                          );
                          if (profileInPerson?.url) {
                            const profUrl = profileInPerson.url.trim();
                            const profLooksUrl =
                              /^https?:\/\//i.test(profUrl) ||
                              /^(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/|$)/i.test(profUrl);
                            if (profLooksUrl) {
                              return (
                                <a
                                  key={link.name}
                                  href={ensureUrlProtocol(profUrl)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-rust"
                                  style={{ padding: "13px 16px", textDecoration: "none", textAlign: "center" }}
                                  onClick={() => { if (!isOwn) void logListingClick(post.id, link.name); }}
                                >
                                  Shop in person · open in maps →
                                </a>
                              );
                            }
                            return (
                              <div
                                key={link.name}
                                style={{
                                  padding: "13px 16px",
                                  border: "1px dashed hsl(var(--ink))",
                                  background: "hsl(var(--paper-2, var(--cream)))",
                                  textAlign: "center",
                                  fontFamily: "'IBM Plex Mono', monospace",
                                  fontSize: 11,
                                  letterSpacing: "0.08em",
                                  textTransform: "uppercase",
                                  color: "hsl(var(--ink-2))",
                                }}
                              >
                                Shop in person · {profUrl}
                              </div>
                            );
                          }
                          // Nothing anywhere → simple "contact seller" label.
                          return (
                            <div
                              key={link.name}
                              style={{
                                padding: "13px 16px",
                                border: "1px dashed hsl(var(--ink))",
                                background: "hsl(var(--paper-2, var(--cream)))",
                                textAlign: "center",
                                fontFamily: "'IBM Plex Mono', monospace",
                                fontSize: 11,
                                letterSpacing: "0.08em",
                                textTransform: "uppercase",
                                color: "hsl(var(--ink-2))",
                              }}
                            >
                              Shop in person · contact seller
                            </div>
                          );
                        }
                        if (isPersonalSite) {
                          // Listing-level URL preferred, fall back to profile.
                          const profilePersonal = post.seller_shops.find(
                            (s) => s.name.toLowerCase() === "personal site",
                          );
                          const target = trimmedUrl || profilePersonal?.url?.trim() || "";
                          if (target) {
                            return (
                              <a
                                key={link.name}
                                href={ensureUrlProtocol(target)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-rust"
                                style={{ padding: "13px 16px", textDecoration: "none", textAlign: "center" }}
                                onClick={() => { if (!isOwn) void logListingClick(post.id, link.name); }}
                              >
                                Visit personal site →
                              </a>
                            );
                          }
                          return (
                            <div
                              key={link.name}
                              style={{
                                padding: "13px 16px",
                                border: "1px dashed hsl(var(--ink))",
                                background: "hsl(var(--paper-2, var(--cream)))",
                                textAlign: "center",
                                fontFamily: "'IBM Plex Mono', monospace",
                                fontSize: 11,
                                letterSpacing: "0.08em",
                                textTransform: "uppercase",
                                color: "hsl(var(--ink-2))",
                              }}
                            >
                              Personal site · no link provided
                            </div>
                          );
                        }
                        // No URL → render as a non-clickable "Listed on X"
                        // label rather than an <a href=""> which resolves
                        // to the current page (and back-nav lands on the
                        // seller's profile, which is what the user was seeing).
                        if (!trimmedUrl) {
                          return (
                            <div
                              key={link.name}
                              style={{
                                padding: "13px 16px",
                                border: "1px dashed hsl(var(--ink))",
                                background: "hsl(var(--paper-2, var(--cream)))",
                                textAlign: "center",
                                fontFamily: "'IBM Plex Mono', monospace",
                                fontSize: 11,
                                letterSpacing: "0.08em",
                                textTransform: "uppercase",
                                color: "hsl(var(--ink-2))",
                              }}
                            >
                              Listed on {link.name} · no link provided
                            </div>
                          );
                        }
                        return (
                          <a
                            key={link.name}
                            href={ensureUrlProtocol(trimmedUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-rust"
                            style={{ padding: "13px 16px", textDecoration: "none", textAlign: "center" }}
                            onClick={() => { if (!isOwn) void logListingClick(post.id, link.name); }}
                          >
                            Shop on {link.name} →
                          </a>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Owner-only: link to full report (verified items) or a
                Verify-now CTA (quick-posted items that never ran through
                fig-verify). The result column being non-null is the
                source of truth for "is this item verified?". */}
            {isOwn && (
              <div style={{ padding: "14px 18px 0" }}>
                <Hairline dashed style={{ marginBottom: 12 }} />
                {post.result ? (
                  <button
                    // /result is mounted at "/result" without an :id param,
                    // so /result/:id 404s. Pass the scan id as a query
                    // param — Result.tsx hydrates from ?scanId= and pulls
                    // the full row from the DB.
                    onClick={() => nav(`/result?scanId=${post.id}`)}
                    style={{
                      background: "none", border: "1px solid hsl(var(--ink))", padding: "11px 14px",
                      cursor: "pointer", width: "100%", textAlign: "left",
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                    }}
                  >
                    <div>
                      <div style={{
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
                        letterSpacing: "0.14em", textTransform: "uppercase",
                        color: "hsl(var(--ink-2))", marginBottom: 2,
                      }}>
                        Owner view
                      </div>
                      <div style={{
                        fontFamily: "'Fraunces', 'Playfair Display', serif",
                        fontWeight: 700, fontSize: 14, color: "hsl(var(--ink))",
                      }}>
                        View full report
                      </div>
                    </div>
                    {Icon.chev(14)}
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      // Verify-now path for quick-posts. Stash the existing
                      // post's id + photos + meta so the scan flow can fetch
                      // the photos, run fig-verify with the user-supplied
                      // brand + category as context, and write the verification
                      // result back onto THIS row instead of creating a
                      // duplicate. Photos are already uploaded (storage URLs)
                      // so no re-capture required — Analyze fetches them down
                      // as base64 inline.
                      try {
                        sessionStorage.setItem("fig_verify_existing_id", post.id);
                        sessionStorage.setItem("fig_verify_existing_photos", JSON.stringify(post.photos ?? []));
                        sessionStorage.setItem("fig_verify_existing_meta", JSON.stringify({
                          title: post.title,
                          brand: post.brand ?? "",
                          category: post.category ?? "",
                        }));
                      } catch { /* quota */ }
                      // Route through /verify-existing — interstitial page
                      // where the user can optionally add a brand-tag /
                      // construction-detail photo before kicking off
                      // verification. From there it forwards to /analyze
                      // with either the combined photo set or the
                      // original-only set.
                      nav("/verify-existing");
                    }}
                    style={{
                      background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))",
                      border: "1px solid hsl(var(--ink))", padding: "11px 14px",
                      cursor: "pointer", width: "100%", textAlign: "left",
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                    }}
                  >
                    <div>
                      <div style={{
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
                        letterSpacing: "0.14em", textTransform: "uppercase",
                        color: "hsl(var(--ink-fg))", opacity: 0.7, marginBottom: 2,
                      }}>
                        Not yet verified
                      </div>
                      <div style={{
                        fontFamily: "'Fraunces', 'Playfair Display', serif",
                        fontWeight: 700, fontSize: 14, color: "hsl(var(--ink-fg))",
                      }}>
                        Verify now →
                      </div>
                    </div>
                  </button>
                )}
              </div>
            )}

            <div style={{ height: 20 }} />

            {/* Action row */}
            <div style={{ padding: "0 18px", display: "flex", gap: 8, flexWrap: "wrap" }}>
              <ActionPill
                label="love"
                count={loves.countFor(post.id)}
                color="hsl(var(--rust))"
                active={loves.mineFor(post.id)}
                disabled={isOwn}
                onClick={() => loves.toggle(post.id, post.user_id)}
              />
              <span style={{ display: "inline-flex" }} onClick={(e) => e.stopPropagation()}>
                <ActionPill
                  label="notes"
                  count={commentCounts.countFor(post.id)}
                  color="hsl(var(--ink-2))"
                  active={showComments}
                  onClick={handleNotesClick}
                />
              </span>
              <button
                onClick={sharePost}
                style={{
                  padding: "5px 9px", border: "1px solid hsl(var(--ink-2))",
                  background: "transparent", color: "hsl(var(--ink-2))", cursor: "pointer",
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                  letterSpacing: "0.08em", textTransform: "uppercase",
                  display: "flex", alignItems: "center", gap: 4,
                }}
              >
                {Icon.share(12)}
                <span>Share</span>
              </button>
            </div>

            {/* Moderator vote bar — only renders for verified mods on
                other users' posts. Sits between the action pills and the
                comments so a reviewing mod's voting decision is the next
                obvious move after looking at the photos. */}
            <div style={{ padding: "0 18px", marginTop: 10 }}>
              <ModeratorVoteBar scanId={post.id} ownerId={post.user_id} />
            </div>

            {/* Comments */}
            <div ref={commentsWrapperRef} style={{ padding: "0 18px" }}>
              <CommentsInline
                scanId={post.id}
                open={showComments}
                onDismiss={() => setShowComments(false)}
                onAdded={(id) => { commentCounts.bump(id, 1); void commentCounts.refreshSingle(id); }}
                onRemoved={(id) => { commentCounts.bump(id, -1); void commentCounts.refreshSingle(id); }}
              />
            </div>

            {/* Overflow menu sheet — portal to body so it escapes the
                MobileFrame zIndex-5 wrapper. Without the portal, the dock
                (zIndex 50, sibling of that wrapper) paints over the
                sheet's bottom buttons. */}
            {menuOpen && createPortal(
              <div
                role="dialog"
                aria-modal="true"
                onClick={() => setMenuOpen(false)}
                style={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 100,
                  background: "rgba(20, 14, 7, 0.6)",
                  display: "flex",
                  alignItems: "flex-end",
                }}
              >
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    width: "100%",
                    background: "hsl(var(--paper))",
                    color: "hsl(var(--ink))",
                    padding: "12px 16px calc(env(safe-area-inset-bottom, 12px) + 12px)",
                    borderTop: "1px solid hsl(var(--ink))",
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  {isOwn && (
                    <button
                      type="button"
                      onClick={() => { setMenuOpen(false); nav(`/listing/${post.id}/edit`); }}
                      style={menuItemStyle}
                    >
                      Edit listing
                    </button>
                  )}
                  {!isOwn && isVerifiedMod && (
                    <button
                      type="button"
                      onClick={() => { setMenuOpen(false); setFfReportOpen(true); }}
                      style={menuItemStyle}
                    >
                      Report as fast fashion
                    </button>
                  )}
                  {!isOwn && isSignedIn && (
                    <button
                      type="button"
                      onClick={() => { setMenuOpen(false); setReportOpen(true); }}
                      style={menuItemStyle}
                    >
                      Report
                    </button>
                  )}
                  {!isOwn && isSignedIn && ownerId && (
                    <button
                      type="button"
                      onClick={async () => {
                        const already = blockedSet?.has(ownerId);
                        if (already) {
                          toast.message("Already blocked");
                          setMenuOpen(false);
                          return;
                        }
                        const ok = window.confirm("Block this user? You won't see their posts or messages.");
                        if (!ok) { setMenuOpen(false); return; }
                        try {
                          await block.mutateAsync(ownerId);
                          toast.success("User blocked");
                        } catch (err: any) {
                          toast.error(err?.message ?? "Couldn't block user");
                        }
                        setMenuOpen(false);
                      }}
                      style={menuItemStyle}
                    >
                      Block this user
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setMenuOpen(false)}
                    style={{
                      ...menuItemStyle,
                      background: "hsl(var(--paper))",
                      marginTop: 6,
                      borderTop: "1px solid hsl(var(--ink))",
                      fontWeight: 600,
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>,
              document.body,
            )}

            <ReportDialog
              open={reportOpen}
              onClose={() => setReportOpen(false)}
              targetKind="post"
              targetId={post.id}
              targetUserId={post.user_id}
            />
            <ReportFastFashionDialog
              open={ffReportOpen}
              onClose={() => setFfReportOpen(false)}
              scanId={post.id}
            />
          </>
        )}
      </div>
    </MobileFrame>
  );
}

function ActionPill({
  label, count, color, active, onClick, disabled,
}: { label: string; count: number; color: string; active?: boolean; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "5px 9px", border: `1px solid ${color}`,
        background: active ? color : "transparent",
        color: active ? "hsl(var(--cream))" : color,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
        fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
        letterSpacing: "0.08em", textTransform: "uppercase",
        display: "flex", alignItems: "center", gap: 5,
      }}
    >
      <span>{label}</span>
      <span style={{ fontWeight: 700 }}>{count}</span>
    </button>
  );
}
